import { useMemo } from 'react';
import Tooltip from '@components/tooltip/tooltip';
import { Controller, useFormContext } from 'react-hook-form';
import Select from 'react-select';
import { customSelectStyles, customSelectStylesNonRequired } from '@forms/input-forms/helpers';
import { Label } from '@trussworks/react-uswds';
import classNames from 'classnames';
import FileDownloadList from '../file-download-list/FileDownloadList';

import './multiSelectInput.scss';

const baseSelectStyles = {
  menu: (base) => ({
    ...base,
    width: '70vw',
  }),
  option: (base, state) => {
    if (state.isDisabled) {
      return {
        ...base,
        color: '#555',
        cursor: 'not-allowed',
      };
    }
    return {
      ...base,
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'unset',
      wordBreak: 'break-word',
    };
  },
};

const groupStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const groupBadgeStyles = {
  backgroundColor: '#EBECF0',
  borderRadius: '2em',
  color: '#172B4D',
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 'normal',
  lineHeight: '1',
  minWidth: 1,
  padding: '0.16666666666667em 0.5em',
  textAlign: 'center',
};

const formatGroupLabel = (data) => (
  <div style={groupStyles}>
    <span>{data.label}</span>
    <span style={groupBadgeStyles}>{data.options.length}</span>
  </div>
);

const MultiSelectInput = ({
  closeMenuOnSelect = false,
  fileDownload,
  filterKeys,
  formatOptionLabel,
  handleBlur,
  handleChange,
  hint,
  isClearable,
  isLoading,
  label,
  menuPlacement,
  menuPosition,
  multi,
  name,
  options,
  readOnly,
  required,
  styles: overrideStyles,
  subtext,
  tooltip,
  tooltipClickable,
}) => {
  const {
    control,
    formState: { errors },
    getValues,
  } = useFormContext();

  const inputError = errors[name];

  const errorStyle = required ? customSelectStyles : customSelectStylesNonRequired;

  const errorStyles = inputError ? errorStyle : {};

  const selectStyles = {
    ...errorStyles,
    ...baseSelectStyles,
    ...(overrideStyles || {}),
  };

  const selectedValues = [getValues(name)] || [];

  const labelStyles = classNames({ required, optional: !required });

  const sortedOptions = useMemo(() => {
    if (!options) return [];

    const allHaveSortOrder = options.every((option) => option.hasOwnProperty('sortOrder'));
    return allHaveSortOrder ? [...options].sort((a, b) => a.sortOrder - b.sortOrder) : options;
  }, [options]);

  const filterOption = (option, inputValue) => {
    const { label, data } = option;
    const search = inputValue?.toLowerCase();
    const labelMatches = label?.toLowerCase().includes(search);

    if (filterKeys && filterKeys.length > 0 && data) {
      const anyKeyMatches = filterKeys.some((key) => {
        const value = data?.[key];
        return typeof value === 'string' && value.toLowerCase().includes(search);
      });
      return labelMatches || anyKeyMatches;
    }

    return labelMatches;
  };

  return (
    <>
      <Label htmlFor={name} className={labelStyles}>
        <span id={`${name}_label`}>{label}</span>
        {tooltip && <Tooltip name={name} header={label} content={tooltip} clickable={tooltipClickable} />}
      </Label>
      {hint && (
        <div className='usa-hint' id={`${name}_hint`}>
          {hint}
        </div>
      )}
      {subtext && <div className='subtext'>{subtext}</div>}
      {fileDownload?.length > 0 && <FileDownloadList files={fileDownload} />}
      {readOnly ? (
        <div className='multi-select-readonly-container'>
          {selectedValues?.flat().map((option, i) => (
            <div key={option?.label || i} className='multi-select-readonly-option'>
              {option?.label}
            </div>
          ))}
        </div>
      ) : (
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, onBlur, value, name, ref } }) => (
            <div className='multiselect-container'>
              <Select
                closeMenuOnSelect={closeMenuOnSelect}
                filterOption={filterOption}
                formatGroupLabel={formatGroupLabel}
                formatOptionLabel={formatOptionLabel}
                hideSelectedOptions={false}
                inputId={name}
                isDisabled={readOnly}
                isLoading={isLoading}
                isMulti={multi}
                menuPlacement={menuPlacement}
                menuPosition={menuPosition}
                name={name}
                onBlur={(e) => {
                  onBlur(e);
                  handleBlur && handleBlur(e);
                }}
                onChange={(e) => {
                  onChange(e);
                  handleChange && handleChange(e);
                }}
                options={sortedOptions}
                readOnly={readOnly}
                required={required}
                ref={ref}
                styles={selectStyles}
                value={value ?? (multi ? [] : null)}
                isClearable={isClearable}
              />
            </div>
          )}
        />
      )}
    </>
  );
};

export default MultiSelectInput;
